<script setup>
import {computed, inject, reactive, ref, toRaw} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog, Listbox} from "primevue";
import {Icon} from "@iconify/vue";
import Tooltip from "@/components/Tooltip/index.vue";
//import stores
import {useUserStore} from "@/stores/user.js";
import {useHotOrdersStore} from "@/stores/hotOrders.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";

//stores
const hotOrdersStore = useHotOrdersStore();
const userStore = useUserStore();
const ordersStore = useMainOrdersStore();

//loading flags
const {
  saving,
  deleting,
  setSaving,
  setDeleting,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();
const confirm = useConfirm();

//emits
const emit = defineEmits(['showUserProfileModal']);

//props
const props = defineProps({
  /*selectedHotOrder: {
    type: Object,
    required: true
  }*/
});

//injects
const visible = inject('isShowAllRollsModal')

//component variables
const selectedTaker = ref(null)

//global functions
//component functions
const selectTaker = (applicant) => {
  if (applicant.id === selectedTaker.value?.id) {
    selectedTaker.value = null
    return
  }
  selectedTaker.value = applicant
}
const onSave = async () => {
  try {
    saving.value = true
    const res = await hotOrdersStore.action_assignTaker('Boost', {
      takerID: selectedTaker.value.id,
      state: 'Assigning',
      id: hotOrdersStore.selectedHotOrder.id
    })
    if (res.result === true) {
      showMessage('Taker set.', 'success');
      setTimeout(() => {
        visible.value = false
      }, 1000)
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

const removeAllApplicants = async () => {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `Do you want to remove all rolls?`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Remove',
      size: 'small'
    },
    accept: async () => {
      try {
        deleting.value = true;
        const res = await hotOrdersStore.action_removeAllApplicants({id: hotOrdersStore.selectedHotOrder.id})
        if (res.result === true) {
          showMessage('All rolls removed.', 'success');
          setTimeout(() => {
            visible.value = false
          }, 1000)
        } else {
          showMessage(res, 'error');
        }
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        deleting.value = false
      }
    },
    reject: () => {
      return
    }
  });
}

function showUserProfileModal(user) {
  emit('showUserProfileModal', user)
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        {{ !hotOrdersStore.selectedHotOrder.takerID ? 'Set Taker' : 'All Rolls' }}
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <button v-if="!hotOrdersStore.selectedHotOrder.takerID" v-tippy="{ content: 'Clear rolls!', theme: 'my-theme' }"
              @click="removeAllApplicants" class="group">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="lucide lucide-brush-cleaning-icon lucide-brush-cleaning">
          <path d="m16 22-1-4"/>
          <path
              d="M19 14a1 1 0 0 0 1-1v-1a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v1a1 1 0 0 0 1 1"/>
          <path d="M19 14H5l-1.973 6.767A1 1 0 0 0 4 22h16a1 1 0 0 0 .973-1.233z"/>
          <path d="m8 22 1-4"/>
        </svg>
      </button>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-hidden pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="flex flex-col text-sm text-slate-300">
        <span>UUID: {{ hotOrdersStore.selectedHotOrder.uuid }}</span>
        <span>Title: {{ hotOrdersStore.selectedHotOrder.title }}</span>
      </div>
      <div class="flex flex-col gap-4 overflow-y-auto">
        <template v-if="hotOrdersStore.selectedHotOrder.applicants.length">
          <div v-for="(applicant, index) in hotOrdersStore.selectedHotOrder.applicants"
               class="flex gap-1 items-center transition-all p-1"
               :class="!hotOrdersStore.selectedHotOrder.takerID ? 'cursor-pointer' : ''"
               @click="selectTaker(applicant)">
            <template v-if="!hotOrdersStore.selectedHotOrder.takerID">
              <svg v-if="applicant.id === selectedTaker?.id" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                   stroke-linejoin="round" class="lucide lucide-check-icon lucide-check">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   class="lucide lucide-circle-icon lucide-circle">
                <circle cx="12" cy="12" r="10"/>
              </svg>
            </template>
            <div class="flex-1 flex gap-6 justify-between text-[.7rem] leading-[1]"
                 :class="[applicant.id === hotOrdersStore.selectedHotOrder.takerID ? '-m-1 p-1 rounded-md bg-[linear-gradient(to_right,_#18373C,_#43E0AD50)]' : '']">
              <div class="flex items-center gap-2">
                <div @click.stop="showUserProfileModal(applicant)" class="cursor-zoom-in select-none">
                  <img v-if="applicant.discordAvatar" :alt="applicant.name"
                       :src="applicant.discordAvatar"
                       class="size-6 rounded-full border border-slate-700"/>
                  <div v-else
                       class="flex items-center justify-center size-6 rounded-full border border-slate-700">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-user-icon lucide-user">
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                </div>
                <div @click.stop class="flex flex-col gap-0.5">
                  <div class="flex flex-row gap-1 items-center">
                    <div class="select-text cursor-text">{{ applicant.name }}</div>
                    <div v-if="applicant.rating" class="flex flex-row items-center gap-0.5">
                      <span class="flex flex-row items-end">
                        {{ applicant.rating }}⭐ ({{ applicant.ratingCount }})
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-row w-max text-slate-400 select-text cursor-text">
                    <div>
                      {{ applicant.discordUsername ?? "-" }}{{ "&nbsp- " + applicant.discordDisplayName ?? "-" }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex flex-row gap-2 items-center">
                <Tooltip v-if="applicant.activeOrdersCount > 0" placement="top" arrow>
                  <template #button>
                    <div class="flex gap-1 items-center">
                      <svg width="14" height="14" viewBox="0 0 512 512">
                        <path fill="none" stroke="currentColor" stroke-miterlimit="10" stroke-width="32"
                              d="M467.51 248.83c-18.4-83.18-45.69-136.24-89.43-149.17A91.5 91.5 0 0 0 352 96c-26.89 0-48.11 16-96 16s-69.15-16-96-16a99 99 0 0 0-27.2 3.66C89 112.59 61.94 165.7 43.33 248.83c-19 84.91-15.56 152 21.58 164.88c26 9 49.25-9.61 71.27-37c25-31.2 55.79-40.8 119.82-40.8s93.62 9.6 118.66 40.8c22 27.41 46.11 45.79 71.42 37.16c41.02-14.01 40.44-79.13 21.43-165.04Z"/>
                        <circle cx="292" cy="224" r="20" fill="currentColor"/>
                        <path fill="currentColor" d="M336 288a20 20 0 1 1 20-19.95A20 20 0 0 1 336 288"/>
                        <circle cx="336" cy="180" r="20" fill="currentColor"/>
                        <circle cx="380" cy="224" r="20" fill="currentColor"/>
                        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                              stroke-width="32" d="M160 176v96m48-48h-96"/>
                      </svg>
                      <span>{{ applicant.activeOrdersCount }}</span>
                    </div>
                  </template>
                  <span class="text-xs">
                    {{ applicant.activeOrdersCount > 1 ? applicant.activeOrdersCount + ' Active Orders' : applicant.activeOrdersCount + ' Active Order' }}
                  </span>
                </Tooltip>
                <div v-if="applicant.activeOrdersCount > 0" class="h-4 w-[1px] bg-slate-600"></div>
                <div class="flex gap-1 items-center">
                  <svg width="14" height="14" viewBox="0 0 128 128">
                    <path fill="#cecece"
                          d="M33.09 16.55s-11.5 4.75-14.2 6.7c-3.29 2.38-7.29 5.41-9.41 9.46c-2.03 3.87-2.84 6.83-2.99 11.79s5.15 46.15 5.15 46.15s28.72 27.06 33.25 29.11s11.21 4.56 19.23 4.41s13.29-2.62 16.36-4.08c3.06-1.46 39.4-55.26 39.7-59.64s.59-12.73.41-16.26c-.19-3.62-.93-6.51-2.83-10.16s-5.02-7.21-6.91-8.82c-1.9-1.6-25.75-12.31-32.97-13.93c-20.86-4.65-44.79 5.27-44.79 5.27"/>
                    <path fill="#eaeaea"
                          d="M15.44 29.1c.14 4.89 4.08 7.15 12.11 11.23S56 56.38 64.46 56.67s39.82-18.77 44.35-21.7c4.48-2.9 5.23-7.53 1.02-10.69c-5.84-4.37-31.95-16.48-37.49-18.52c-2.83-1.04-10.04-2.66-15.76-.44c-4.52 1.75-33.19 15.02-35.96 16.77s-5.25 4.28-5.18 7.01"/>
                    <path fill="#fff"
                          d="M34.34 19.12c-1.78-2.01-14.31 4.55-14.31 10.14c0 5.42 11.37 12.32 14.22 9.76c2.05-1.84-5.21-7.58-4.26-10.9c.94-3.32 5.87-7.3 4.35-9m12.4 28.96c-1.97 2.85 10.62 8.98 18.3 8.98c7.23 0 19.04-7.06 17.52-9.11c-2.46-3.32-8.43-.2-17.72 0c-5.12.11-16.05-2.84-18.1.13m45.67-9.04c2.46 1.61 16.49-5.5 16.77-9.76s-8.72-9.38-11.28-7.58s.76 3.13-.09 6.25s-8.35 9.16-5.4 11.09"/>
                    <path fill="#dfdfdf"
                          d="M6.48 45.09c-.15 5.17.16 36.73.23 41.01c.07 4.72 1.23 9.97 4.37 12.66s28.57 18.82 34.29 21.29c8.01 3.46 15.8-.34 16.36-8.96c.56-8.63.22-34.96.22-38.54s-1.8-8.86-5.72-11.55s-34.92-20.47-38.1-22.3c-4.48-2.57-11.42-1.34-11.65 6.39"/>
                    <path fill="#b8b8b8"
                          d="M70.68 61.34c-3.36 2.66-4.71 6.5-4.71 10.64s-.22 36.08-.11 39.33s4.71 11.88 11.88 9.75c7.41-2.2 16.81-8.85 23.53-12.89c6.72-4.03 11.96-7.45 14.3-9.37c3-2.45 5.03-8.38 5.14-13.42c.11-4.93-.03-35.79-.12-38.21c-.22-5.68-2.52-10.37-8.68-9.03S75.5 57.53 70.68 61.34"/>
                    <ellipse cx="64.29" cy="29.27" fill="#fe4e27" rx="13.81" ry="9.93"/>
                    <path fill="#2f2f2f"
                          d="M26.49 55.55c1.8 5.02 1.26 10.22-2.59 11.6s-8.46-2.05-10.23-6.96c-1.81-5.02-1.36-10.23 2.59-11.6c3.87-1.33 8.43 1.94 10.23 6.96M51.82 99.6c2.14 4.88 2.02 10.66-1.72 12.3s-8.81-1.77-10.92-6.55c-2.15-4.88-1.83-10.23 1.91-11.87c3.75-1.64 8.59 1.24 10.73 6.12m50.14-32.64c3.27 1.86 8.53-.45 11.24-5.19c2.71-4.75 2.25-10.1-1.01-11.97c-3.27-1.86-8.44.44-11.15 5.19c-2.71 4.74-2.35 10.11.92 11.97M89.04 88.7c3.32 1.94 8.72-.37 11.54-5.2c2.82-4.82 2.42-10.31-.9-12.25s-8.83.21-11.65 5.03c-2.82 4.84-2.3 10.48 1.01 12.42m-12.83 22.22c3.21 1.96 8.54-.2 11.39-4.86s2.55-10.03-.66-11.99s-8.18-.58-11.46 4.61c-2.92 4.61-2.48 10.28.73 12.24"/>
                  </svg>
                  <span>{{ applicant.roll }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
        <p v-else class="text-center">No one has ever rolled.</p>
      </div>
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave" v-if="!hotOrdersStore.selectedHotOrder.takerID" :disabled="!selectedTaker"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Assign
      </button>
      <button v-else @click="visible = false"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Close
      </button>
    </div>
  </Dialog>
</template>
<style></style>